import * as Sentry from "@sentry/nextjs"
import type { BaseTierId, AddOnId, Gender } from "./types"
import type { PriceMap } from "@/lib/pricing"

export function basePriceKey(tier: BaseTierId): keyof PriceMap {
  switch (tier) {
    case "blood_only":
      return "blood"
    case "advanced_mri":
      return "advanced"
    case "pro_mri":
      return "pro"
  }
}

export function addOnPriceKey(
  id: AddOnId,
  gender: Gender | undefined
): keyof PriceMap | null {
  switch (id) {
    case "hormone_test":
      return gender === "female" ? "addonHormoneWomen" : "addonHormoneMen"
    case "omega_test":
      return "addonOmega"
    case "b_vitamins":
      return "addonBVitamins"
    case "calcium_score":
      return "addonHeartCT"
    case "dexa_scan":
      return "addonDEXA"
    case "mamma_mri":
      return "addonMammaMRI"
    case "trend_overlay":
      return null
  }
}

/**
 * Sum of the base tier + every priced add-on, in the map's currency.
 *
 * Returns `NaN` when the map is missing (or carries a non-numeric value for) any
 * key the recommendation needs — the total is then genuinely unknowable, and a
 * bogus partial sum would be worse than no figure. Callers must guard with
 * `Number.isFinite` (UI) / `finiteOrUndefined` (the completion event) and omit
 * the price rather than ship it: `Number(undefined)` → `NaN` → `JSON.stringify`
 * → `null`, which 400'd the `marketing_quiz/completed` submission against the
 * `.strict()` `z.number()` schema and produced no event, no email and no Slack
 * ping at all (EAGNT-233; email-flows PR #632 patched the same class downstream).
 *
 * A **populated** map missing a specific key is a get-products feed gap (Stripe/
 * Supabase didn't return a product the quiz recommends) — surfaced to Sentry
 * naming the missing key(s) so the gap is visible instead of silently swallowed.
 * An **empty** map is the documented pre-launch EUR case (`fetchPrices` returns
 * `{}` for a market that isn't LAUNCHED — hide the money, don't fabricate it), so
 * it stays silent.
 */
export function totalPrice(
  prices: PriceMap,
  baseTier: BaseTierId,
  addOns: AddOnId[],
  gender: Gender | undefined
): number {
  const keys: (keyof PriceMap)[] = [basePriceKey(baseTier)]
  for (const id of addOns) {
    const key = addOnPriceKey(id, gender)
    if (key !== null) keys.push(key)
  }

  const missing: string[] = []
  let sum = 0
  for (const key of keys) {
    const value = Number(prices[key])
    if (Number.isFinite(value)) {
      sum += value
    } else {
      missing.push(String(key))
    }
  }

  if (missing.length > 0) {
    // Only a populated-but-incomplete map is a real feed gap. A fully empty map
    // is the expected pre-launch EUR market and would just be Sentry noise.
    if (Object.keys(prices).length > 0) {
      const detail = `quiz totalPrice: get-products feed is missing price key(s) [${missing.join(
        ", "
      )}] for base tier "${baseTier}" — total omitted`
      // eslint-disable-next-line no-console
      console.warn(detail)
      Sentry.captureMessage(detail, {
        level: "warning",
        tags: { surface: "quiz-total-price" },
        extra: { missingKeys: missing, baseTier, addOns },
      })
    }
    return NaN
  }

  return sum
}
